import { LinkOutlined } from '@ant-design/icons';
import { ModalForm, ProFormSelect, ProTable } from '@ant-design/pro-components';
import { useMutation } from '@tanstack/react-query';
import { App, Button } from 'antd';
import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import {
  approveOrganizationExternalIdentity,
  queryExternalTenantCandidates,
  queryExternalTenantUsers,
  queryOrganizationExternalIdentities,
} from '../services/organizations';

const externalProviderOptions = [
  {
    label: '飞书',
    value: 'feishu',
  },
  {
    label: '企业微信',
    value: 'wecom',
  },
  {
    label: '钉钉',
    value: 'dingtalk',
  },
];

const providerValueEnum = Object.fromEntries(
  externalProviderOptions.map((option) => [option.value, { text: option.label }]),
);

const UpdateOrganizationExternalIdentityForm = ({ organization }) => {
  const actionRef = useRef(null);
  const formRef = useRef(null);
  const [externalProvider, setExternalProvider] = useState('feishu');
  const [externalTenantKey, setExternalTenantKey] = useState();
  const { message } = App.useApp();
  const { mutateAsync: approve, isPending: approving } = useMutation({
    mutationFn: approveOrganizationExternalIdentity,
    onSuccess: () => {
      actionRef.current?.reload?.();
      message.success('三方租户映射审核已通过');
    },
    onError: () => {
      message.error('三方租户映射审核失败，请重试');
    },
  });

  const columns = [
    {
      title: '三方平台名称',
      dataIndex: 'externalProvider',
      valueEnum: providerValueEnum,
    },
    {
      title: '三方租户标识',
      dataIndex: 'externalTenantKey',
      copyable: true,
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      valueType: 'dateTime',
    },
  ];

  return (
    <ModalForm
      title="三方租户映射审核"
      trigger={
        <Button type="link" icon={<LinkOutlined />}>
          查看三方映射
        </Button>
      }
      width={720}
      formRef={formRef}
      initialValues={{ externalProvider: 'feishu', role: 'owner' }}
      modalProps={{
        destroyOnHidden: true,
        okButtonProps: {
          loading: approving,
        },
      }}
      onFinish={async (values) => {
        try {
          await approve({
            data: {
              organizationId: organization.id,
              ...values,
            },
          });
          return true;
        } catch {
          return false;
        }
      }}
    >
      <ProFormSelect
        name="externalProvider"
        label="三方平台名称"
        options={externalProviderOptions}
        fieldProps={{
          onChange: (value) => {
            setExternalProvider(value);
            setExternalTenantKey(undefined);
            formRef.current?.setFieldsValue?.({
              externalTenantKey: undefined,
              externalUserId: undefined,
            });
          },
        }}
        rules={[
          {
            required: true,
            message: '请选择三方平台名称',
          },
        ]}
      />
      <ProFormSelect
        name="externalTenantKey"
        label="待审核三方租户标识"
        params={{ externalProvider }}
        request={async ({ externalProvider: provider }) => {
          if (!provider) {
            return [];
          }
          const result = await queryExternalTenantCandidates({ externalProvider: provider });
          return (result.data || []).map((item) => ({
            label: item.externalTenantName
              ? `${item.externalTenantName}（${item.externalTenantKey}）`
              : item.externalTenantKey,
            value: item.externalTenantKey,
          }));
        }}
        fieldProps={{
          onChange: (value) => {
            setExternalTenantKey(value);
            formRef.current?.setFieldsValue?.({
              externalUserId: undefined,
            });
          },
        }}
        rules={[
          {
            required: true,
            message: '请选择待审核三方租户标识',
          },
        ]}
      />
      <ProFormSelect
        name="externalUserId"
        label="初始管理员"
        params={{ externalProvider, externalTenantKey }}
        request={async ({ externalProvider: provider, externalTenantKey: tenantKey }) => {
          if (!provider || !tenantKey) {
            return [];
          }
          const result = await queryExternalTenantUsers({
            externalProvider: provider,
            externalTenantKey: tenantKey,
          });
          return (result.data || []).map((item) => ({
            label: item.name || item.externalUserId,
            value: item.externalUserId,
          }));
        }}
        rules={[
          {
            required: true,
            message: '请选择初始管理员',
          },
        ]}
      />
      <ProFormSelect
        name="role"
        label="初始角色"
        options={[
          { label: '所有者', value: 'owner' },
          { label: '管理员', value: 'admin' },
        ]}
        rules={[
          {
            required: true,
            message: '请选择初始角色',
          },
        ]}
      />
      <ProTable
        actionRef={actionRef}
        rowKey="id"
        headerTitle="已配置映射"
        request={async () => {
          const result = await queryOrganizationExternalIdentities({
            organizationId: organization.id,
          });
          return {
            data: result.data || [],
            success: true,
          };
        }}
        columns={columns}
        pagination={false}
        search={false}
        options={false}
      />
    </ModalForm>
  );
};

UpdateOrganizationExternalIdentityForm.propTypes = {
  organization: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
};

export default UpdateOrganizationExternalIdentityForm;
